<template>
  <v-card outlined class="mb-4 pa-4">
    <v-row align="center" dense>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="searchQuery"
          label="Búsqueda por Folio o Cliente..."
          prepend-inner-icon="mdi-magnify"
          hide-details
          outlined
          dense
          clearable
          @keyup.enter="emitSearchEvent"
        ></v-text-field>
      </v-col>

      <v-col cols="12" md="2">
        <v-select
          v-model="statusId"
          :items="statusOptions"
          label="Estatus"
          prepend-inner-icon="mdi-filter-variant"
          hide-details
          outlined
          dense
          clearable
        ></v-select>
      </v-col>

      <v-col cols="12" md="2">
        <v-menu
          v-model="menuStart"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="startDate"
              label="Compra Desde"
              prepend-inner-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              hide-details
              outlined
              dense
              clearable
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="startDate"
            :max="maxStartDateAllowed"
            @input="menuStart = false"
            locale="es-mx"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="12" md="2">
        <v-menu
          v-model="menuEnd"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="endDate"
              label="Compra Hasta"
              prepend-inner-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              hide-details
              outlined
              dense
              clearable
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="endDate"
            :min="minEndDateAllowed"
            @input="menuEnd = false"
            locale="es-mx"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="12" md="2" class="d-flex align-center">
        <v-btn
          color="grey darken-3"
          text
          block
          height="40"
          class="text-none font-weight-medium justify-center"
          :disabled="!isAnyFilterActive"
          @click="clearFilters"
        >
          <v-icon left size="20">mdi-filter-remove-outline</v-icon>
          Limpiar filtros
        </v-btn>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
export default {
  name: 'DeliverySearchBar',

  props: {
    initialSearch: { type: String, default: '' },
    initialStart: { type: String, default: null },
    initialEnd: { type: String, default: null },
    initialStatus: { type: [Number, String], default: null },
    currentTab: { type: String, default: 'pending' }, // 'pending' o 'history'
  },

  data() {
    return {
      searchQuery: this.initialSearch || '',
      startDate: this.initialStart || null,
      endDate: this.initialEnd || null,
      statusId: this.initialStatus || null,
      menuStart: false,
      menuEnd: false,
      debounceTimeout: null,
      isInternalChange: false,
    };
  },

  computed: {
    statusOptions() {
      if (this.currentTab === 'history') {
        return [
          { text: 'Pendiente', value: 1 },
          { text: 'Surtido Parcial', value: 2 },
          { text: 'Entregado Total', value: 3 },
        ];
      }
      // Pestaña Pendientes
      return [
        { text: 'Pendiente', value: 2 },
        { text: 'En Tránsito', value: 3 },
        { text: 'Listo para Entrega', value: 4 },
      ];
    },

    isAnyFilterActive() {
      return (
        !!this.searchQuery || !!this.startDate || !!this.endDate || !!this.statusId
      );
    },

    // Prevención de Errores: La fecha final no puede ser menor a la inicial
    minEndDateAllowed() {
      return this.startDate || undefined;
    },

    // Prevención de Errores: La fecha inicial no puede ser mayor a la final
    maxStartDateAllowed() {
      return this.endDate || undefined;
    },
  },

  watch: {
    currentTab() {
      this.statusId = null;
    },
    initialSearch(newVal) {
      if (newVal !== this.searchQuery) {
        this.isInternalChange = true;
        this.searchQuery = newVal || '';
      }
    },
    initialStart(newVal) {
      if (newVal !== this.startDate) {
        this.isInternalChange = true;
        this.startDate = newVal || null;
      }
    },
    initialEnd(newVal) {
      if (newVal !== this.endDate) {
        this.isInternalChange = true;
        this.endDate = newVal || null;
      }
    },
    initialStatus(newVal) {
      if (newVal !== this.statusId) {
        this.isInternalChange = true;
        this.statusId = newVal || null;
      }
    },

    searchQuery(newVal) {
      if (this.isInternalChange) {
        this.isInternalChange = false;
        return;
      }
      if (!newVal || newVal.trim().length >= 3) {
        this.debouncedSearch();
      }
    },
    startDate() {
      if (this.isInternalChange) {
        this.isInternalChange = false;
        return;
      }
      this.debouncedSearch();
    },
    endDate() {
      if (this.isInternalChange) {
        this.isInternalChange = false;
        return;
      }
      this.debouncedSearch();
    },
    statusId() {
      if (this.isInternalChange) {
        this.isInternalChange = false;
        return;
      }
      this.debouncedSearch();
    },
  },

  beforeDestroy() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  },

  methods: {
    debouncedSearch() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      this.debounceTimeout = setTimeout(() => {
        this.emitSearchEvent();
      }, 500);
    },

    emitSearchEvent() {
      this.$emit('search', {
        search: this.searchQuery ? this.searchQuery.trim() : '',
        startDate: this.startDate,
        endDate: this.endDate,
        statusId: this.statusId,
      });
    },

    clearFilters() {
      this.isInternalChange = false;
      this.searchQuery = '';
      this.startDate = null;
      this.endDate = null;
      this.statusId = null;

      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      this.emitSearchEvent();

      this.$emit('reset-focus');
    },
  },
};
</script>
